import { useAdminUsers } from '../../hooks/admin/useAdminQueries'
import { useAdminStore } from '../../stores/adminStore'
import { AdminUserDto } from '../../types/admin-types'
import UserActionsModal from './UserActionsModal'
import RunResearchModal from './research-results/RunResearchModal'
import React, { useState } from 'react'

const UserList: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [hoveredUserId, setHoveredUserId] = useState<string | null>(null)
    const [showActionsModal, setShowActionsModal] = useState(false)
    const [showRunResearchModal, setShowRunResearchModal] = useState(false)
    const [selectedUserForModal, setSelectedUserForModal] = useState<AdminUserDto | null>(null)
    const [isAllUsersSelectedForModal, setIsAllUsersSelectedForModal] = useState(false)

    // Get state and actions from Zustand store
    const selectedUserId = useAdminStore((state) => state.selectedUserId)
    const setSelectedUserId = useAdminStore((state) => state.setSelectedUserId)
    const openAddUserModal = useAdminStore((state) => state.openAddUserModal)
    const adminView = useAdminStore((state) => state.adminView) // Get current admin view
    // showUserList and toggleUserList are still needed for collapse/expand
    const showUserList = useAdminStore((state) => state.showUserList)
    const toggleUserList = useAdminStore((state) => state.toggleUserList)

    const {
        data: usersData,
        isLoading: usersLoading,
        isError: usersIsError,
        error: usersError,
    } = useAdminUsers() // Consider if different params needed for 'users' view later

    const handleUserSelect = (userId: string) => {
        setSelectedUserId(userId)
    }

    const handleAllUsersSelect = () => {
        // Only relevant in content view or research view for potential future bulk actions
        if (adminView === 'content' || adminView === 'research') {
            setSelectedUserId(null)
        }
    }

    // Updated handler for showing action modals
    const handleShowActionModal = (user: AdminUserDto | null, isAllUsers: boolean = false) => {
        setSelectedUserForModal(user)
        setIsAllUsersSelectedForModal(isAllUsers)

        if (isAllUsers) {
            if (adminView === 'content') {
                // Show original actions modal for bulk content actions
                setShowActionsModal(true)
            } else if (adminView === 'research') {
                // Show the research modal configured for bulk run
                setShowRunResearchModal(true)
            } else {
                console.log('No bulk actions defined for this view.')
            }
        } else {
            // Individual user actions
            if (adminView === 'content') {
                setShowActionsModal(true) // Original actions modal
            } else if (adminView === 'research') {
                setShowRunResearchModal(true) // Research modal for single user
            } else if (adminView === 'users') {
                // In users view, clicking might open edit, handled differently (e.g., in UserDetail)
                // Or potentially add different actions here later.
                console.log('User clicked in User Management view - typically opens detail.')
            }
        }
    }

    const filteredUsers = (usersData?.users || []).filter((user) => {
        if (!searchTerm.trim()) return true
        const lowerSearchTerm = searchTerm.toLowerCase()
        return (
            user.name.toLowerCase().includes(lowerSearchTerm) ||
            user.email.toLowerCase().includes(lowerSearchTerm)
        )
    })

    // Determine if the Actions button should be shown for the list item
    const shouldShowActionButton = (view: string) => {
        return view === 'content' || view === 'research'
    }

    if (!showUserList) {
        return (
            <div className="w-10 flex flex-col items-center py-4 bg-white border-r border-gray-200">
                <button
                    onClick={toggleUserList}
                    className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-800"
                    title="Show Users"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path
                            fillRule="evenodd"
                            d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                            clipRule="evenodd"
                        />
                    </svg>
                </button>
            </div>
        )
    }

    return (
        <div className="w-64 border-r border-gray-200 h-full bg-white relative flex flex-col flex-shrink-0">
            <div className="p-4 border-b border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Users</h2>
                    <button
                        onClick={toggleUserList}
                        className="p-1 rounded hover:bg-gray-200"
                        title="Hide Users"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                </div>
                <input
                    type="text"
                    placeholder="Search users..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="flex-grow overflow-y-auto p-4">
                {usersLoading ? (
                    <div className="text-center text-gray-500">Loading users...</div>
                ) : usersIsError ? (
                    <div className="text-center text-red-500">
                        Error: {usersError?.message || 'Unknown error'}
                    </div>
                ) : (
                    <ul className="space-y-1">
                        {/* "All Users" List Item */}
                        {(adminView === 'content' || adminView === 'research') && (
                            <li
                                className={`p-2 rounded cursor-pointer relative group ${
                                    selectedUserId === null
                                        ? 'bg-blue-100 font-semibold'
                                        : 'hover:bg-gray-100'
                                } ${
                                    shouldShowActionButton(adminView) ? 'hover:pr-16' : ''
                                } transition-colors`}
                                onClick={handleAllUsersSelect}
                                onMouseEnter={() => setHoveredUserId('all')}
                                onMouseLeave={() => setHoveredUserId(null)}
                            >
                                All Users
                                {/* Show Actions button only if relevant for the view */}
                                {hoveredUserId === 'all' && shouldShowActionButton(adminView) && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleShowActionModal(null, true) // Trigger modal for All Users
                                        }}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-opacity duration-150"
                                    >
                                        Actions
                                    </button>
                                )}
                            </li>
                        )}
                        {filteredUsers.map((user) => (
                            <li
                                key={user.id}
                                className={`p-2 rounded cursor-pointer relative group ${
                                    selectedUserId === user.id
                                        ? 'bg-blue-100 font-semibold'
                                        : 'hover:bg-gray-100'
                                } ${
                                    shouldShowActionButton(adminView) ? 'hover:pr-16' : ''
                                } transition-colors`}
                                onClick={() => handleUserSelect(user.id)}
                                onMouseEnter={() => setHoveredUserId(user.id)}
                                onMouseLeave={() => setHoveredUserId(null)}
                            >
                                <div className="font-medium truncate">{user.name}</div>
                                <div className="text-sm text-gray-500 truncate">{user.email}</div>
                                {hoveredUserId === user.id && shouldShowActionButton(adminView) && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleShowActionModal(user, false)
                                        }}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-opacity duration-150"
                                    >
                                        Actions
                                    </button>
                                )}
                            </li>
                        ))}
                        {filteredUsers.length === 0 && !usersLoading && (
                            <li className="p-2 text-gray-500 text-center">No users found</li>
                        )}
                    </ul>
                )}
            </div>

            {adminView === 'users' && (
                <div className="p-4 border-t border-gray-200 space-y-2">
                    <button
                        onClick={openAddUserModal}
                        className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                    >
                        + Add User
                    </button>
                </div>
            )}

            <UserActionsModal
                show={showActionsModal}
                onClose={() => setShowActionsModal(false)}
                user={selectedUserForModal}
                isAllUsers={isAllUsersSelectedForModal}
                users={usersData?.users || []}
            />

            <RunResearchModal
                show={showRunResearchModal}
                onClose={() => setShowRunResearchModal(false)}
                user={selectedUserForModal}
                isBulkRun={isAllUsersSelectedForModal}
                allUserIds={
                    isAllUsersSelectedForModal ? usersData?.users.map((u) => u.id) : undefined
                }
            />
        </div>
    )
}

export default UserList
